import React, { useState } from 'react';
import {
  Modal,
  TextField,
  InlineStack,
  Text,
  Banner,
  Spinner,
  BlockStack
} from '@shopify/polaris';

const ImportShopifyOrderModal = ({ 
  isOpen, 
  onClose, 
  onImport,
  country,
  store,
  terminal = null,
  loading = false,
  error = null 
}) => {
  const [shopifyOrderId, setShopifyOrderId] = useState('');
  const [validationError, setValidationError] = useState('');

  const handleOrderIdChange = (value) => {
    setShopifyOrderId(value);
    // Clear validation error when user starts typing
    if (validationError) {
      setValidationError('');
    }
  };

  const handleImport = () => {
    // Validate order ID
    if (!shopifyOrderId.trim()) {
      setValidationError('Please enter a Shopify order ID');
      return;
    }

    // Basic validation for Shopify order ID format (should be numeric)
    if (!/^\d+$/.test(shopifyOrderId.trim())) {
      setValidationError('Shopify order ID should be numeric');
      return;
    }

    onImport(shopifyOrderId.trim());
  };

  const handleClose = () => {
    setShopifyOrderId('');
    setValidationError('');
    onClose();
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      title={<div style={{padding: '16px'}}>{'Import Order from Shopify'}</div>}
      primaryAction={{
        content: loading ? 'Importing...' : 'Import Order',
        onAction: handleImport,
        loading: loading,
        disabled: loading || !shopifyOrderId.trim()
      }}
      secondaryActions={[
        {
          content: 'Cancel',
          onAction: handleClose,
          disabled: loading
        }
      ]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          <Text variant="bodyMd">
            Enter the Shopify order ID. Customer, item, SKU, line, and
            printing lane selection always come from the selected Shopify store.
          </Text>

          <Banner status="info">
            <BlockStack gap="100">
              <Text variant="bodyMd">Country: {country}</Text>
              <Text variant="bodyMd">Store: {store}</Text>
            </BlockStack>
          </Banner>

          {error && (
            <Banner status="critical">
              <Text variant="bodyMd">{error}</Text>
            </Banner>
          )}

          {terminal && (
            <Banner status="success" title="Import completed">
              <BlockStack gap="100">
                <Text variant="bodyMd">
                  Result: {terminal.result?.status || 'completed'}
                </Text>
                <Text variant="bodyMd">
                  Order: {terminal.result?.order_id || 'available'}
                </Text>
                <Text variant="bodyMd">
                  Request: {terminal.request_id}
                </Text>
              </BlockStack>
            </Banner>
          )}

          <TextField
            label="Shopify Order ID"
            value={shopifyOrderId}
            onChange={handleOrderIdChange}
            placeholder="e.g., 1234567890"
            error={validationError}
            helpText="Enter the numeric order ID from Shopify"
            autoComplete="off"
            disabled={loading}
          />

          {loading && (
            <InlineStack gap="200" align="center">
              <Spinner size="small" />
              <Text variant="bodyMd" color="subdued">
                Fetching order details from Shopify...
              </Text>
            </InlineStack>
          )}
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
};

export default ImportShopifyOrderModal;